import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleHelp, PlayCircle } from 'lucide-react';
import { dicaPara } from '../lib/ajuda-campos';

const ESPERA_MS = 1000;
const CHAVE = 'erp_dicas';
const ALVOS = '[data-ajuda],button,a,label,input,select,textarea,[role="tab"]';

export function dicasLigadas(): boolean {
  try {
    return localStorage.getItem(CHAVE) !== 'off';
  } catch {
    return true;
  }
}
export function ligarDicas(ligar: boolean) {
  try {
    localStorage.setItem(CHAVE, ligar ? 'on' : 'off');
  } catch {
    /* navegação privada: vale só nesta sessão */
  }
}

/** Texto de ajuda de um elemento: `data-ajuda` primeiro, depois o rótulo/texto conhecido. */
export function dicaDoElemento(el: HTMLElement): string | null {
  if (el.dataset.ajuda) return el.dataset.ajuda;
  if (
    el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement
  )
    return (
      dicaPara(el.labels?.[0]?.textContent) ??
      dicaPara(el.getAttribute('aria-label')) ??
      dicaPara(el.getAttribute('placeholder'))
    );
  if (el instanceof HTMLLabelElement && el.control)
    return dicaDoElemento(el.control as HTMLElement);
  return dicaPara(el.getAttribute('aria-label')) ?? dicaPara(el.textContent);
}

/** Rótulo visível do elemento (para buscar o artigo/vídeo na Ajuda). */
function rotuloDo(el: HTMLElement): string {
  const t =
    (el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement
      ? el.labels?.[0]?.textContent
      : null) ??
    el.getAttribute('aria-label') ??
    el.textContent ??
    '';
  return t.replace(/[*:?]/g, '').replace(/\s+/g, ' ').trim().slice(0, 40);
}

/**
 * Para o mouse sobre um campo ou botão por 1 segundo: aparece um (?) explicando para que serve.
 * Um único ouvinte na página inteira — nenhuma tela precisa ser alterada; para uma dica
 * específica basta `data-ajuda="..."` no elemento. Só com mouse (no celular atrapalharia o toque).
 */
export function AjudaFlutuante() {
  const [dica, setDica] = useState<{ texto: string; termo: string; x: number; y: number } | null>(
    null,
  );
  const [ligadas, setLigadas] = useState(dicasLigadas);
  const timer = useRef<number>(undefined);
  const fechar = useRef<number>(undefined);

  useEffect(() => {
    if (!ligadas) return;
    const limpar = () => {
      window.clearTimeout(timer.current);
      fechar.current = window.setTimeout(() => setDica(null), 200);
    };
    const sobre = (e: PointerEvent) => {
      if (e.pointerType && e.pointerType !== 'mouse') return; // toque e caneta não
      const alvo = (e.target as HTMLElement | null)?.closest<HTMLElement>(ALVOS);
      if (!alvo || alvo.closest('[data-ajuda-balao]')) return;
      window.clearTimeout(timer.current);
      const texto = dicaDoElemento(alvo);
      if (!texto) return;
      timer.current = window.setTimeout(() => {
        const r = alvo.getBoundingClientRect();
        window.clearTimeout(fechar.current);
        setDica({
          texto,
          termo: rotuloDo(alvo),
          x: Math.min(Math.max(8, r.left), window.innerWidth - 296),
          y: r.bottom + 8 + 120 > window.innerHeight ? r.top - 8 : r.bottom + 8,
        });
      }, ESPERA_MS);
    };
    const fora = (e: PointerEvent) => {
      if ((e.relatedTarget as HTMLElement | null)?.closest?.('[data-ajuda-balao]')) return;
      limpar();
    };
    const sumir = () => {
      window.clearTimeout(timer.current);
      setDica(null);
    };
    document.addEventListener('pointerover', sobre);
    document.addEventListener('pointerout', fora);
    document.addEventListener('pointerdown', sumir);
    document.addEventListener('keydown', sumir);
    window.addEventListener('scroll', sumir, true);
    return () => {
      sumir();
      document.removeEventListener('pointerover', sobre);
      document.removeEventListener('pointerout', fora);
      document.removeEventListener('pointerdown', sumir);
      document.removeEventListener('keydown', sumir);
      window.removeEventListener('scroll', sumir, true);
    };
  }, [ligadas]);

  if (!dica) return null;
  const acima = dica.y < 0 || dica.y > window.innerHeight - 128;
  return (
    <div
      data-ajuda-balao
      role="tooltip"
      onPointerEnter={() => window.clearTimeout(fechar.current)}
      onPointerLeave={() => setDica(null)}
      onPointerDown={(e) => e.stopPropagation()}
      style={{ left: dica.x, top: dica.y, transform: acima ? 'translateY(-100%)' : undefined }}
      className="fixed z-[100] flex w-72 max-w-[calc(100vw-16px)] gap-2 rounded-lg border bg-popover p-3 text-sm text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95"
    >
      <CircleHelp className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
      <div className="flex flex-col gap-1">
        <p>{dica.texto}</p>
        {dica.termo && (
          <Link
            to={`/ajuda?busca=${encodeURIComponent(dica.termo)}`}
            onClick={() => setDica(null)}
            className="inline-flex items-center gap-1 self-start text-xs font-medium text-primary hover:underline"
          >
            <PlayCircle className="size-3.5" aria-hidden /> Ver tutorial na Ajuda
          </Link>
        )}
        <button
          type="button"
          className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => {
            ligarDicas(false);
            setLigadas(false);
            setDica(null);
          }}
        >
          Não mostrar dicas (religa em Ajuda)
        </button>
      </div>
    </div>
  );
}
